import React from 'react';
import {
  Box, Portal, Snackbar, Typography,
} from '@mui/material';
import { useMutation } from '@apollo/client';
import { UPDATE_PET_PARENT_PROFILE } from '../../../queries';
import { phoneFormatter } from '../../../utilities';
import { Awo } from '../../../types/Awo';
import { useHubPetParent } from '../HubPetParentContext';
import HubSectionContainer from '../CustomContainer';
import PersonalInfoCard from './PersonalInfoCard';
import CommunicationPreferenceCard from './CommunicationPreferenceCard';
import MembershipCard from './MembershipCard';
import getHubAudience from '../hubAudience';
import { normalizePhone, PersonalInfoValues } from './validation';
import { track } from '../analytics';
import useHubPageView from '../useHubPageView';

const VARIANT_BY_AUDIENCE = {
  adopter: undefined,
  preSurrender: 'community',
  foster: 'foster',
} as const;

function Settings() {
  useHubPageView();
  const { petParent, loading, refetch } = useHubPetParent();
  const [updateProfile] = useMutation(UPDATE_PET_PARENT_PROFILE);
  const [snackbar, setSnackbar] = React.useState('');

  if (loading || !petParent) return null;

  const country = (petParent.country || petParent.awo?.country) as Awo['country'];
  const formattedPhone = phoneFormatter(petParent.phoneNumber, country);

  const handleSaveProfile = async (values: PersonalInfoValues) => {
    track('personal_info_save_click');
    const variables: Record<string, unknown> = { id: petParent.id };
    if (values.firstName !== petParent.firstName) variables.firstName = values.firstName;
    if (values.lastName !== petParent.lastName) variables.lastName = values.lastName;
    if (values.email !== petParent.email) variables.email = values.email;
    const normalized = normalizePhone(values.phoneNumber, country);
    if (normalized !== normalizePhone(petParent.phoneNumber, country)) {
      variables.phoneNumber = normalized;
    }
    try {
      await updateProfile({ variables });
      await refetch();
      setSnackbar('Changes saved');
    } catch {
      setSnackbar('Failed to save changes');
    }
  };

  const handlePrefChange = async (smsPreferred: boolean) => {
    track('comm_preference_change', { preference: smsPreferred ? 'sms' : 'email' });
    try {
      await updateProfile({ variables: { id: petParent.id, smsPreferred } });
      await refetch();
      setSnackbar('Changes saved');
    } catch (e) {
      setSnackbar('Failed to save changes');
      throw e; // let the card revert its optimistic selection
    }
  };

  return (
    <>
      <Box sx={{ borderBottom: '1px solid #E0E0E0' }}>
        <HubSectionContainer wide sx={{ gap: 0.5, py: 3 }}>
          <Typography variant="sectionTitle">Settings</Typography>
          <Typography variant="body2">Manage your account, preferences, and membership.</Typography>
        </HubSectionContainer>
      </Box>

      <Box sx={{ background: '#F6F5FA', pb: 4, flexGrow: 1 }}>
        <HubSectionContainer wide backgroundContrast sx={{ py: 3 }}>
          <Box sx={{
            maxWidth: 720, width: '100%', mx: 'auto', display: 'flex', flexDirection: 'column', gap: 3,
          }}
          >
            <PersonalInfoCard
              values={{
                firstName: petParent.firstName || '',
                lastName: petParent.lastName || '',
                phoneNumber: formattedPhone,
                email: petParent.email || '',
              }}
              onSave={handleSaveProfile}
            />

            <CommunicationPreferenceCard
              email={petParent.email}
              phoneNumber={formattedPhone}
              smsPreferred={petParent.smsPreferred}
              onChange={handlePrefChange}
            />

            <MembershipCard
              status={petParent.membershipStatus}
              expiryDate={petParent.membershipExpiryDate}
              planName={petParent.membershipPlanName}
              isPremium={petParent.isPremium}
              variant={VARIANT_BY_AUDIENCE[getHubAudience(petParent)]}
              providedBy={petParent.awo?.displayName ?? petParent.awo?.name}
            />
          </Box>
        </HubSectionContainer>
      </Box>

      <Portal container={() => document.getElementById('root')}>
        <Snackbar
          open={Boolean(snackbar)}
          autoHideDuration={2000}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          message={snackbar}
          onClose={() => setSnackbar('')}
          onClick={() => setSnackbar('')}
        />
      </Portal>
    </>
  );
}

export default Settings;
